// Admin dashboard — pool overview + peer table with actions

import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { DataTable } from "../components/DataTable.js";
import { StatusBadge } from "../components/StatusBadge.js";
import type { Peer } from "../lib/api-client.js";
import { usePeerStore } from "../stores/peers.js";

export function PeerDashboard() {
	const [, setLocation] = useLocation();
	const { peers, poolStatus, loading, error, filter, fetchPeers, fetchPoolStatus, revokePeer, initializePool, setFilter } =
		usePeerStore();

	const [revokeTarget, setRevokeTarget] = useState<string | null>(null);
	const [revokeReason, setRevokeReason] = useState("");

	useEffect(() => {
		fetchPeers();
		fetchPoolStatus();
	}, [fetchPeers, fetchPoolStatus]);

	const handleRevoke = async () => {
		if (revokeTarget) {
			await revokePeer(revokeTarget, revokeReason || undefined);
			setRevokeTarget(null);
			setRevokeReason("");
		}
	};

	const columns = [
		{
			key: "ip",
			header: "IP Address",
			render: (p: Peer) => <span className="mono">{p.ipAddress}</span>,
		},
		{
			key: "status",
			header: "Status",
			render: (p: Peer) => <StatusBadge status={p.status} />,
		},
		{
			key: "assignedTo",
			header: "Assigned To",
			render: (p: Peer) => p.assignedTo || "—",
		},
		{
			key: "assignedAt",
			header: "Assigned At",
			render: (p: Peer) =>
				p.assignedAt ? new Date(p.assignedAt).toLocaleDateString() : "—",
		},
		{
			key: "actions",
			header: "Actions",
			render: (p: Peer) => (
				<div style={{ display: "flex", gap: "var(--space-2)" }}>
					{p.status === "unassigned" && (
						<button
							className="btn btn-sm btn-primary"
							onClick={() => setLocation(`/peers/${p.ipAddress}/assign`)}
							type="button"
						>
							Assign
						</button>
					)}
					{p.status === "assigned" && (
						<>
							<button
								className="btn btn-sm btn-danger"
								onClick={() => setRevokeTarget(p.ipAddress)}
								type="button"
							>
								Revoke
							</button>
							<button
								className="btn btn-sm"
								onClick={() => window.open(`/api/v1/peers/${p.ipAddress}/config`, "_blank")}
								type="button"
							>
								Config
							</button>
						</>
					)}
					{p.status === "revoked" && (
						<button
							className="btn btn-sm btn-primary"
							onClick={() => setLocation(`/peers/${p.ipAddress}/assign`)}
							type="button"
						>
							Reassign
						</button>
					)}
				</div>
			),
		},
	];

	return (
		<div>
			<h1 className="page-title">Peer Dashboard</h1>

			{/* Pool Summary Cards */}
			{poolStatus && (
				<div className="card-grid">
					<div className="card card-metric">
						<div className="card-metric-value">{poolStatus.total}</div>
						<div className="card-metric-label">Total</div>
					</div>
					<div className="card card-metric">
						<div className="card-metric-value" style={{ color: "var(--color-pending)" }}>
							{poolStatus.unassigned}
						</div>
						<div className="card-metric-label">Unassigned</div>
					</div>
					<div className="card card-metric">
						<div className="card-metric-value" style={{ color: "var(--color-success)" }}>
							{poolStatus.assigned}
						</div>
						<div className="card-metric-label">Assigned</div>
					</div>
					<div className="card card-metric">
						<div className="card-metric-value" style={{ color: "var(--color-error)" }}>
							{poolStatus.revoked}
						</div>
						<div className="card-metric-label">Revoked</div>
					</div>
				</div>
			)}

			{/* Toolbar */}
			<div className="toolbar">
				<select
					className="form-input"
					style={{ width: "auto" }}
					value={filter.status || ""}
					onChange={(e) => setFilter({ status: e.target.value || undefined })}
				>
					<option value="">All Statuses</option>
					<option value="unassigned">Unassigned</option>
					<option value="assigned">Assigned</option>
					<option value="revoked">Revoked</option>
				</select>
				<input
					className="form-input"
					style={{ width: "250px" }}
					type="text"
					placeholder="Search IP or user..."
					value={filter.search || ""}
					onChange={(e) => setFilter({ search: e.target.value || undefined })}
				/>
				{poolStatus && poolStatus.total === 0 && (
					<button className="btn btn-primary" onClick={() => initializePool()} type="button">
						Initialize Pool
					</button>
				)}
			</div>

			{error && <div className="card" style={{ color: "var(--color-error)" }}>{error}</div>}
			{loading && <div style={{ color: "var(--color-ink-muted)" }}>Loading...</div>}

			<DataTable columns={columns} data={peers} rowKey={(p) => p.ipAddress} />

			{/* Revoke confirmation */}
			<ConfirmDialog
				open={revokeTarget !== null}
				title="Revoke Peer"
				confirmLabel="Revoke"
				confirmVariant="danger"
				onConfirm={handleRevoke}
				onCancel={() => {
					setRevokeTarget(null);
					setRevokeReason("");
				}}
			>
				<p>
					Revoke peer <strong className="mono">{revokeTarget}</strong>? The user will lose VPN
					access.
				</p>
				<div className="form-group" style={{ marginTop: "var(--space-4)" }}>
					<label className="form-label">Reason (optional)</label>
					<input
						className="form-input"
						type="text"
						value={revokeReason}
						onChange={(e) => setRevokeReason(e.target.value)}
						placeholder="e.g., Employee offboarding"
					/>
				</div>
			</ConfirmDialog>
		</div>
	);
}
